// Řezy / clipping (§16). Zpřístupňuje ořez modelu přes globální clippingPlanes
// hlavního rendereru. API je záměrně malé a stabilní (enable/create/clear/list);
// souřadnice se zadávají v IFC prostoru a převádějí na scénu, aby hostitel nemusel
// znát interní offset federace.
import * as THREE from "three";

export class Sections {
  constructor(core, emit) {
    this._core = core;
    this._emit = emit;
    this._planes = [];
    this._enabled = false;
  }

  get renderer() {
    const r = this._core.world && this._core.world.renderer;
    return r ? r.three : null;
  }

  enable() {
    this._enabled = true;
    const r = this.renderer;
    if (r) { r.localClippingEnabled = true; r.clippingPlanes = this._planes; }
    this._changed();
    return this;
  }

  disable() {
    this._enabled = false;
    const r = this.renderer;
    if (r) r.clippingPlanes = [];
    this._changed();
    return this;
  }

  // create({ axis:'x'|'y'|'z', position:number, flip?:bool }) — vodorovný ('z') i
  // svislé ('x','y') řezy. position je IFC souřadnice v ose. Vrací id roviny.
  create(opts = {}) {
    const axis = opts.axis || "z";
    const pos = typeof opts.position === "number" ? opts.position : 0;
    // Bod na rovině v IFC → scéna.
    const ifcPoint = { x: 0, y: 0, z: 0 };
    ifcPoint[axis] = pos;
    const scenePoint = this._ifcToScene(ifcPoint);
    // Normála v IFC ose → scéna (bez posunu). IFC(x,y,z) → scéna(x, z, -y).
    const nIfc = { x: 0, y: 0, z: 0 };
    nIfc[axis] = opts.flip ? -1 : 1;
    const normal = new THREE.Vector3(nIfc.x, nIfc.z, -nIfc.y).normalize();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, scenePoint);
    const id = "sec" + (this._planes.length + 1) + "-" + Date.now().toString(36);
    plane.userData = { id, axis, position: pos };
    this._planes.push(plane);
    if (this._enabled) { const r = this.renderer; if (r) r.clippingPlanes = this._planes; }
    else this.enable();
    this._changed();
    return id;
  }

  remove(id) {
    this._planes = this._planes.filter((p) => p.userData && p.userData.id !== id);
    const r = this.renderer; if (r) r.clippingPlanes = this._enabled ? this._planes : [];
    this._changed();
  }

  clear() {
    this._planes = [];
    const r = this.renderer; if (r) r.clippingPlanes = [];
    this._changed();
  }

  list() {
    return this._planes.map((p) => ({ id: p.userData.id, axis: p.userData.axis, position: p.userData.position }));
  }

  // --- Obecné roviny (BCF) — location + direction v IFC souřadnicích ---------
  // Přidá řeznou rovinu z BCF (bod + normála v IFC prostoru).
  addWorldPlane(location, direction) {
    const scenePoint = this._ifcToScene(location);
    const normal = this._ifcDirToScene(direction).normalize();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, scenePoint);
    const id = "sec" + (this._planes.length + 1) + "-" + Date.now().toString(36);
    plane.userData = { id };
    this._planes.push(plane);
    if (!this._enabled) this.enable();
    else { const r = this.renderer; if (r) r.clippingPlanes = this._planes; }
    this._changed();
    return id;
  }

  // Vrátí všechny řezné roviny jako { location, direction } v IFC souřadnicích.
  getPlanes() {
    return this._planes.map((p) => {
      const pt = p.coplanarPoint(new THREE.Vector3());
      return { location: this._sceneToIfc(pt), direction: this._sceneDirToIfc(p.normal) };
    });
  }

  // Nahradí všechny roviny sadou z IFC souřadnic (pro aplikaci BCF viewpointu).
  setPlanes(planes) {
    this.clear();
    for (const pl of planes || []) if (pl && pl.location && pl.direction) this.addWorldPlane(pl.location, pl.direction);
  }

  _ifcToScene(p) {
    const off = this._core.federationOffset;
    return new THREE.Vector3(p.x, p.z, -p.y).add(off);
  }
  _sceneToIfc(p) {
    const off = this._core.federationOffset;
    return { x: p.x - off.x, y: -(p.z - off.z), z: p.y - off.y };
  }
  _ifcDirToScene(d) { return new THREE.Vector3(d.x, d.z, -d.y); }
  _sceneDirToIfc(d) { return { x: d.x, y: -d.z, z: d.y }; }
  _changed() { if (this._emit) this._emit("sectionChanged", { count: this._planes.length, enabled: this._enabled }); }
}
